/* eslint-disable */
import { canonicalUrl } from "@/config/canonical";
import { useSeoCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import { WingsCupBanner } from "@/features/WingsCup/WingsCup";
import { useGridColumnCount } from "@/hooks/useGridColumnCount";
import { globalStateSelector } from "@/redux/globalState/globalStateSlice";
import { generateNavigationUrl } from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";
import { SBC_CATEGORIES, sbcSetPath } from "@repo/shared/utils";
import { Fragment, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useSearchParams } from "react-router";

import SBCSet from "../../components/organisms/SBCs/SBCSet/SBCSet";
import { trackViewPlayerContent } from "../../events/sbcMixpanel";
import { addAlert, showModal } from "../../redux/message/messageSlice";
import {
  ALL_CATEGORY,
  FAVORITES_CATEGORY,
  SBCCategory,
  SBCSetSortOption,
  SbcSet,
  fetchSbcsSets,
  getFilteredSbcSetsSelector,
  getSbcSetsSelector,
  hideSbcSet,
  markSbcSetAsFavorite,
  markSbcSetSolved,
  updateCategory,
  updateSearchTerm,
  updateSort,
} from "../../redux/sbcs/sbcSetsSlice";
import { AppDispatch } from "../../redux/store";
import { getUserSelector } from "../../redux/user/userSlice";
import "../Subscription/Subscription.css";
import { handleVote } from "./SbcSetPage/handlers/sbcSetHandlers";
import {
  SBC_SORT_OPTIONS,
  SbcSetStatus,
  SbcSetsPageToolbar,
} from "./SbcSetsPageToolbar";

const SbcSetsPage = () => {
  const { t } = useTranslation("sbcs");
  const sbcsSeo = useSeoCopy("sbcs");
  const sbcSetsReducer = useSelector(getSbcSetsSelector);
  const { currentCategory, searchTerm, currentSort, sbcsSets } = sbcSetsReducer;
  const { user } = useSelector(getUserSelector);

  const { allSbcs, notCompletedSbcs, completedSbcs, hiddenSbcs } = useSelector(
    getFilteredSbcSetsSelector,
  );
  const { status } = sbcsSets;
  const { lastImportFinishedAt } = useSelector(globalStateSelector);
  const dispatch = useDispatch<AppDispatch>();

  const [searchParams, setSearchParams] = useSearchParams();
  /* One list at a time rather than three stacked accordions: Completed and
     Hidden are places you go looking for a set, not things you scroll past.
     In the URL so coming back from a set keeps it. */
  const statusParam = searchParams.get("status");
  const statusFilter: SbcSetStatus =
    statusParam === "all" ||
    statusParam === "completed" ||
    statusParam === "hidden"
      ? statusParam
      : "available";
  const navigate = useNavigate();
  const [gridRef, columns] = useGridColumnCount();

  // The route loader fetches on mount; a club import syncs the user's SBC
  // progress, so its Done fetches again.
  const importFinishedAtMount = useRef(lastImportFinishedAt);
  useEffect(() => {
    if (lastImportFinishedAt !== importFinishedAtMount.current)
      dispatch(fetchSbcsSets());
  }, [dispatch, lastImportFinishedAt]);

  useEffect(() => {
    if (searchParams.get("sort")) {
      const sort = SBC_SORT_OPTIONS.find(
        (option) => option.value === searchParams.get("sort"),
      ) as SBCSetSortOption;
      if (sort) {
        dispatch(updateSort(sort));
      }
    }
    // ?category= is all, favorites or the EA category id; the English name
    // older links carry still resolves.
    const categoryParam = searchParams.get("category")?.toLowerCase();
    if (categoryParam) {
      const category: SBCCategory | undefined =
        categoryParam === "all"
          ? ALL_CATEGORY
          : categoryParam === "favorites"
            ? FAVORITES_CATEGORY
            : /^\d+$/.test(categoryParam)
              ? { label: "", value: Number(categoryParam) }
              : SBC_CATEGORIES.find(
                  (c) => c.label.toLowerCase() === categoryParam,
                );
      if (category) {
        dispatch(updateCategory(category));
      }
    }
  }, []);

  const onMarkSBCSetAsFavorite = (
    setId: number,
    setName: string,
    markFavorite: boolean,
  ) => {
    if (user.uuid) {
      dispatch(
        markSbcSetAsFavorite({
          setId,
          isFavorite: markFavorite,
        }),
      );
      dispatch(
        addAlert({
          description: markFavorite
            ? t("alerts.markedFavorite", { name: setName })
            : t("alerts.removedFavorite", { name: setName }),
          type: "success",
        }),
      );
    } else
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "favorite-sbc",
        }),
      );
  };

  const onHideSBCSet = (setId: number, setName: string, hide: boolean) => {
    if (user.uuid) {
      dispatch(
        hideSbcSet({
          setId,
          isHidden: hide,
        }),
      );
      dispatch(
        addAlert({
          description: hide
            ? t("alerts.markedHidden", { name: setName })
            : t("alerts.removedHidden", { name: setName }),
          type: "success",
        }),
      );
    } else
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "hide-sbc",
        }),
      );
  };

  const onCompleteSBCSet = (
    setId: number,
    setName: string,
    didComplete: boolean,
  ) => {
    if (user.uuid) {
      dispatch(markSbcSetSolved({ setId, didComplete }));
      const description = didComplete
        ? t("alerts.completed", { name: setName })
        : t("alerts.removedCompleted", { name: setName });
      dispatch(
        addAlert({
          description,
          type: "success",
        }),
      );
    } else
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "complete-sbc",
        }),
      );
  };

  const handleCategoryChanged = (category: SBCCategory) => {
    setSearchParams(
      {
        ...Object.fromEntries(searchParams.entries()),
        category:
          category.value === undefined
            ? "favorites"
            : category.value < 0
              ? "all"
              : String(category.value),
      },
      { replace: true },
    );
    dispatch(updateCategory(category));
  };

  const handleStatusChanged = (next: SbcSetStatus) => {
    const params = new URLSearchParams(searchParams);
    if (next === "available") params.delete("status");
    else params.set("status", next);
    setSearchParams(params, { replace: true });
  };

  const handleSortByChanged = (option: SBCSetSortOption) => {
    setSearchParams(
      {
        ...Object.fromEntries(searchParams.entries()),
        sort: option.value,
      },
      { replace: true },
    );
    dispatch(updateSort(option));
  };

  const setsByStatus: Record<SbcSetStatus, SbcSet[]> = {
    all: allSbcs,
    available: notCompletedSbcs,
    completed: completedSbcs,
    hidden: hiddenSbcs,
  };
  const sets = setsByStatus[statusFilter];

  const handleSearch = (searchTerm: string) => {
    dispatch(updateSearchTerm(searchTerm));
  };

  const onVote = (setId: number, vote: "UPVOTE" | "DOWNVOTE") => {
    const sbcSet = sbcSetsReducer.sbcsSets.data.find((set) => set.id === setId);
    handleVote(dispatch, sbcSet, vote, user.uuid);
  };

  const getSbcCard = (sbcSet: SbcSet) => (
    <SBCSet
      key={sbcSet.id}
      {...sbcSet}
      to={sbcSetPath(sbcSet)}
      solvedCount={
        sbcSet.repeatabilityMode !== "UNLIMITED" ? sbcSet.solvedCount : 0
      }
      price={user.platform === "PC" ? sbcSet.pcPrice : sbcSet.psPrice}
      isCompleted={
        sbcSet.solvedCount === sbcSet.sbcsCount &&
        sbcSet.repeatabilityMode !== "UNLIMITED"
      }
      onCompleteSBCSet={() => {
        onCompleteSBCSet(sbcSet.id, sbcSet.name, !sbcSet.solved);
      }}
      onHideSBCSet={() => {
        onHideSBCSet(sbcSet.id, sbcSet.name, !sbcSet.isHidden);
      }}
      onMarkSBCSetAsFavorite={() => {
        onMarkSBCSetAsFavorite(sbcSet.id, sbcSet.name, !sbcSet.isFavorite);
      }}
      onUpvote={() => onVote(sbcSet.id, "UPVOTE")}
      onDownvote={() => onVote(sbcSet.id, "DOWNVOTE")}
    />
  );

  return (
    <main className="z-10 h-full min-h-screen flex-1 bg-background text-white">
      <HeroSection
        helmetTitle={sbcsSeo.title}
        helmetDescription={sbcsSeo.description}
        helmetCanonicalLink={canonicalUrl("/sbc")}
        shouldBeInTheDocumentFlow={true}
      />
      {/* <TOTSBanner /> */}
      {/* <FutBirthdayBanner /> */}

      {/* {!user.paid && <RootBanner />} */}
      <SbcSetsPageToolbar
        currentSortBy={currentSort}
        handleSortByChanged={handleSortByChanged}
        handleSearch={handleSearch}
        searchTerm={searchTerm}
        currentCategory={currentCategory}
        handleCategoryChanged={handleCategoryChanged}
        status={statusFilter}
        onStatusChanged={handleStatusChanged}
        statusCounts={{
          all: allSbcs.length,
          available: notCompletedSbcs.length,
          completed: completedSbcs.length,
          hidden: hiddenSbcs.length,
        }}
      />
      {sets.length > 0 ? (
        <section
          ref={gridRef}
          className="mx-auto grid w-full max-w-content grid-cols-1 gap-4 px-4 pb-12 pt-4 desktop:grid-cols-2 large-desktop:grid-cols-3 large-desktop:px-8"
        >
          {sets.map((sbcSet, index) => (
            <Fragment key={sbcSet.id}>
              {getSbcCard(sbcSet)}
              {/* After the first row, however many cards fit in it. */}
              {index === Math.min(columns, sets.length) - 1 && (
                <WingsCupBanner unit="sbc" className="col-span-full" />
              )}
            </Fragment>
          ))}
        </section>
      ) : (
        <p className="mt-4 min-h-[50vh] text-center text-sm text-foreground-muted">
          {status === "pending" || status === "idle" ? "" : t("setList.empty")}
        </p>
      )}
    </main>
  );
};

export default SbcSetsPage;
